'use client';

import { useEffect, useMemo, useState } from 'react';

import { updateSearchParams } from '@/components/SearchResultsFacets/utils';
import { SEARCH_SERVICE_PROVIDERS } from '@/constants/grs';
import { getKeyValue } from '@/services/content/data';
import { useSearchContext } from '@/src/services/search/SearchProvider';
import type { SearchPageConfigProperties } from '@/types/contentStack';
import { FacetBucket } from '@/types/searchQuery';
import { Select, Skeleton } from '@nxt-costco-com/forge-components';
import {
    Analytics,
    type ContentStackEntryDataProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

import { StyledSortContainer, StyledSortSelect } from './styles';

export function SortSelector({
    pageConfig,
}: {
    pageConfig: ContentStackEntryDataProps<SearchPageConfigProperties>;
}) {
    const { queryParams: searchParams, setQueryParams } = useQueryParams();
    const { searchResult, searchService, setSortType } = useSearchContext();
    const [selectedValue, setSelectedValue] = useState<
        string | null | undefined
    >(searchParams.get('sortBy'));
    const [sortValueSet, setSortValueSet] = useState(false);
    const properties = pageConfig.properties[0];

    // Memoize buckets to prevent useEffect dependencies from changing on every render
    const buckets: FacetBucket[] = useMemo(() => {
        const initialBuckets = properties?.buckets || [];

        if (searchService === SEARCH_SERVICE_PROVIDERS.GRS) {
            return initialBuckets.filter(
                bucket => bucket.key !== 'sortkey.mostviewed',
            );
        }

        return initialBuckets;
    }, [properties?.buckets, searchService]);

    useEffect(() => {
        const key: string | undefined = buckets.find(
            element => element.meta === selectedValue,
        )?.key;

        if (selectedValue != null && selectedValue && key) {
            setSortType(getKeyValue(key, pageConfig));
        }
    }, [pageConfig, selectedValue, buckets, setSortType]);

    useEffect(() => {
        const sortBy = searchParams.get('sortBy');
        if (sortBy) {
            setSelectedValue(sortBy);
            setSortValueSet(true);
        } else if (
            searchResult &&
            (searchResult.defaultFusionSort || buckets[0]!.meta)
        ) {
            setSelectedValue(
                searchResult?.defaultFusionSort
                    ? searchResult?.defaultFusionSort.replace(
                          new RegExp(' ', 'g'),
                          '+',
                      )
                    : buckets[0]!.meta,
            );
            setSortValueSet(true);
        }
    }, [searchResult, searchParams, buckets]);

    const handleDoSort = (meta: string) => {
        setSelectedValue(meta);
        const params = new URLSearchParams(searchParams);

        params.set('sortBy', meta);
        params.delete('currentPage');

        updateSearchParams(setQueryParams, params);
    };

    if (!properties) {
        return null;
    }

    return (
        <form style={{ maxWidth: '300px', width: '100%' }}>
            <StyledSortContainer>
                <StyledSortSelect>
                    <Analytics
                        analyticData={{
                            component: {
                                identifier: 'sort by',
                                type: 'ui',
                            },
                            trackType: 'navigation',
                        }}
                    >
                        {sortValueSet || selectedValue ? (
                            <Select
                                label={getKeyValue('sortlabel', pageConfig)}
                                options={buckets.map(entry => ({
                                    text: getKeyValue(entry.key, pageConfig)!,
                                    value: entry.meta,
                                }))}
                                value={selectedValue || ''}
                                onChange={e => {
                                    handleDoSort(e.target.value as string);
                                }}
                                uniqueId="sort_by"
                                errorIconAriaLabel=""
                            />
                        ) : (
                            // @ts-expect-error TODO: export variants or move to const
                            <Skeleton variant="quantitySelector" height={40} />
                        )}
                    </Analytics>
                </StyledSortSelect>
            </StyledSortContainer>
        </form>
    );
}
